// Shared Gemini AI helper for Hayah features.
//
// Public API:
//   Key/model accessors:   getGeminiKey/saveGeminiKey,
//                          getGeminiModel/saveGeminiModel, GEMINI_MODELS
//   One-shot:              callGemini(prompt, opts),
//                          callGeminiVision(prompt, image, opts)
//   Multi-turn:            callGeminiChat(messages, opts)
//   Streaming (SSE):       callGeminiChatStream(messages, opts),
//                          callGeminiVisionStream(prompt, image, opts)
//   Self-test:             testGemini(key, modelId)
//
// Opts (all optional):
//   systemInstruction   string  — sent as systemInstruction.parts[].text
//   responseSchema      object  — Gemini structured-output schema; implies JSON
//   jsonMode            bool    — responseMimeType: "application/json" w/o schema
//   model               string  — overrides getGeminiModel()
//   maxOutputTokens     number
//   temperature         number
//   feature             string  — telemetry bucket name (aiTokenLog)
//   onChunk             func    — streaming only: (chunk, accumulated) => void
//
// Resilience: a single quick-retry on the primary model (1.5s), then walks
// the FALLBACK_CHAINS map from aiConfig.js on retryable errors. Per-call-site
// retry loops are no longer required; v1 had them and they were redundant.

import { FALLBACK_CHAINS } from "./hayah/aiConfig";
import { recordUsage } from "./hayah/aiTokenLog";

const GEMINI_KEY_STORAGE   = "hayah-gemini-key";
const GEMINI_MODEL_STORAGE = "hayah-gemini-model";

export const GEMINI_MODELS = [
  { id: "gemini-2.5-pro",             label: "Gemini 2.5 Pro",         hint: "Highest quality · most expensive (~4x Flash)" },
  { id: "gemini-2.5-flash",           label: "Gemini 2.5 Flash",       hint: "Balanced · AI Coach default" },
  { id: "gemini-2.5-flash-lite",      label: "Gemini 2.5 Flash Lite",  hint: "Fastest · cheapest · lower reasoning quality" },
];
const DEFAULT_MODEL = "gemini-2.5-flash";
const GEMINI_BASE   = "https://generativelanguage.googleapis.com/v1beta/models";

// Retryable error patterns — Gemini surfaces "high demand", "overloaded",
// "unavailable", etc. when throttling. Network / 5xx fall in here too.
const RETRYABLE = /high demand|overload|unavailable|temporary|503|429|quota|rate.?limit|load failed|failed to fetch|networkerror/i;

// ── Storage accessors ────────────────────────────────────────────────────
export function getGeminiModel() {
  try {
    const stored = localStorage.getItem(GEMINI_MODEL_STORAGE);
    if (stored && GEMINI_MODELS.some(m => m.id === stored)) return stored;
    return DEFAULT_MODEL;
  } catch { return DEFAULT_MODEL; }
}
export function saveGeminiModel(id) {
  try { localStorage.setItem(GEMINI_MODEL_STORAGE, id); } catch {}
}
export function getGeminiKey() {
  try { return localStorage.getItem(GEMINI_KEY_STORAGE) || ""; } catch { return ""; }
}
export function saveGeminiKey(key) {
  try { localStorage.setItem(GEMINI_KEY_STORAGE, (key || "").trim()); } catch {}
}

// ── Body builder (shared by every call shape) ────────────────────────────
function buildBody({ contents, systemInstruction, responseSchema, jsonMode, maxOutputTokens, temperature, thinkingConfig }) {
  const cfg = {};
  if (temperature != null) cfg.temperature = temperature;
  if (maxOutputTokens != null) cfg.maxOutputTokens = maxOutputTokens;
  if (responseSchema) {
    cfg.responseMimeType = "application/json";
    cfg.responseSchema = responseSchema;
  } else if (jsonMode) {
    cfg.responseMimeType = "application/json";
  }
  // Pass-through for Gemini 2.5 thinking budget. Setting
  // { thinkingBudget: 0 } disables reasoning entirely — vital when the
  // caller needs every output token for structured JSON and can't afford
  // thinking tokens silently eating the budget.
  if (thinkingConfig) cfg.thinkingConfig = thinkingConfig;
  return {
    contents,
    ...(systemInstruction ? { systemInstruction: { parts: [{ text: systemInstruction }] } } : {}),
    generationConfig: cfg,
  };
}

// Convert a chat history entry into Gemini content parts. Supports
// in-history image attachments (v1 silently dropped these).
function messagePartsFor(m) {
  if (m.image && typeof m.image === "string" && m.image.startsWith("data:")) {
    const [header, data] = m.image.split(",");
    const mime = header.replace("data:", "").replace(";base64", "");
    const parts = [];
    if (m.text) parts.push({ text: m.text });
    parts.push({ inline_data: { mime_type: mime, data } });
    return parts;
  }
  return [{ text: m.text || "" }];
}

// ── Non-streaming attempt (one HTTP call) ────────────────────────────────
async function attemptGemini(modelId, key, body) {
  const resp = await fetch(
    `${GEMINI_BASE}/${modelId}:generateContent?key=${key}`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
  );
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try { const e = await resp.json(); msg = e?.error?.message || msg; } catch {}
    throw new Error(msg.slice(0, 200));
  }
  const data = await resp.json();
  const cand = data?.candidates?.[0];
  const text = cand?.content?.parts?.[0]?.text;
  if (!text) {
    const finish = cand?.finishReason || "unknown";
    const thought = data?.usageMetadata?.thoughtsTokenCount;
    const detail = finish === "MAX_TOKENS"
      ? `Gemini hit token cap before producing output (finishReason=MAX_TOKENS${thought ? `, thoughts=${thought}` : ""}). Raise maxOutputTokens.`
      : `Empty response from Gemini (finishReason=${finish})`;
    throw new Error(detail);
  }
  return { text, usage: data?.usageMetadata || null, raw: data };
}

// Walks primary → fallbacks on retryable errors. Non-retryable errors
// throw immediately. Token usage from whichever attempt succeeded is
// recorded against `feature`.
// Hard ceiling for the auto-retry token bump. Pro can technically accept
// higher, but past ~16K the request becomes too slow / expensive to retry
// transparently — we'd rather surface the error and let the caller raise
// its own cap.
const MAX_TOKEN_RETRY_CEILING = 16384;
// Catches both the explicit MAX_TOKENS finishReason AND the Gemini 2.5
// Pro "Empty response (finishReason=STOP)" quirk where thinking tokens
// silently exhaust the budget before any visible output starts. Both
// resolve via the same doubling-and-retrying strategy.
const MAX_TOKENS_RE = /MAX_TOKENS|token cap before producing output|Empty response from Gemini \(finishReason=STOP\)/i;

async function callGeminiResilient(primaryModel, body, key, feature, onUsage) {
  const chain = [primaryModel, ...(FALLBACK_CHAINS[primaryModel] || [])];
  let lastErr;
  for (let i = 0; i < chain.length; i++) {
    try {
      let result;
      try {
        result = await attemptGemini(chain[i], key, body);
      } catch (e) {
        // Quick retry on primary only — transient 429/503 often clears in <2s.
        if (i === 0 && RETRYABLE.test(e.message || "")) {
          await new Promise(r => setTimeout(r, 1500));
          result = await attemptGemini(chain[i], key, body);
        } else if (MAX_TOKENS_RE.test(e.message || "") && body?.generationConfig?.maxOutputTokens) {
          // Auto-retry on MAX_TOKENS — Gemini 2.5 Pro/Flash use opaque
          // "thinking" budgets that can swallow most of a small cap before
          // any output appears. Double the budget once (capped at
          // MAX_TOKEN_RETRY_CEILING) and retry on the SAME model. This is
          // transparent recovery: the caller never sees the error.
          const cur = body.generationConfig.maxOutputTokens;
          const next = Math.min(cur * 2, MAX_TOKEN_RETRY_CEILING);
          if (next > cur) {
            body.generationConfig.maxOutputTokens = next;
            result = await attemptGemini(chain[i], key, body);
          } else {
            throw e;
          }
        } else {
          throw e;
        }
      }
      if (feature && result.usage) {
        try { recordUsage(feature, result.usage, chain[i]); } catch {}
      }
      // Surface per-call usage + the model that actually answered (which
      // may not be the primary if fallbacks fired). Callers use this to
      // show a small per-message cost indicator.
      if (typeof onUsage === "function") {
        try { onUsage({ usage: result.usage || null, modelId: chain[i] }); } catch {}
      }
      return result.text;
    } catch (e) {
      lastErr = e;
      if (!RETRYABLE.test(e.message || "")) throw e;
      if (i < chain.length - 1) await new Promise(r => setTimeout(r, 800));
    }
  }
  throw lastErr || new Error("Gemini request failed");
}

// ── ONE-SHOT TEXT ────────────────────────────────────────────────────────
export async function callGemini(userPrompt, {
  systemInstruction = null,
  responseSchema = null,
  jsonMode = false,
  model = null,
  maxOutputTokens = 1024,
  temperature = 0.4,
  feature = null,
  onUsage = null,
  thinkingConfig = null,
} = {}) {
  const resolvedModel = model || getGeminiModel();
  const key = getGeminiKey();
  if (!key) throw new Error("No Gemini API key. Add one in Hayah Settings → AI Assistant.");
  const body = buildBody({
    contents: [{ role: "user", parts: [{ text: userPrompt }] }],
    systemInstruction, responseSchema, jsonMode, maxOutputTokens, temperature, thinkingConfig,
  });
  return await callGeminiResilient(resolvedModel, body, key, feature, onUsage);
}

// ── VISION (one-shot) ────────────────────────────────────────────────────
export async function callGeminiVision(userPrompt, imageDataUrl, {
  model = null,
  maxOutputTokens = 2048,
  systemInstruction = null,
  responseSchema = null,
  jsonMode = false,
  temperature = 0.3,
  feature = null,
  onUsage = null,
} = {}) {
  const resolvedModel = model || getGeminiModel();
  const key = getGeminiKey();
  if (!key) throw new Error("No Gemini API key. Add one in Hayah Settings → AI Assistant.");
  let mimeType = "image/jpeg", base64Data = imageDataUrl;
  if (imageDataUrl.startsWith("data:")) {
    const [header, data] = imageDataUrl.split(",");
    mimeType = header.replace("data:", "").replace(";base64", "");
    base64Data = data;
  }
  const body = buildBody({
    contents: [{
      role: "user",
      parts: [
        { text: userPrompt },
        { inline_data: { mime_type: mimeType, data: base64Data } },
      ],
    }],
    systemInstruction, responseSchema, jsonMode, maxOutputTokens, temperature,
  });
  return await callGeminiResilient(resolvedModel, body, key, feature, onUsage);
}

// ── MULTI-TURN CHAT ──────────────────────────────────────────────────────
// messages is [{ role: "user" | "ai" | "model", text: string, image?: string }]
export async function callGeminiChat(messages, {
  systemInstruction = null,
  responseSchema = null,
  jsonMode = false,
  model = null,
  maxOutputTokens = 1024,
  temperature = 0.7,
  feature = null,
  onUsage = null,
} = {}) {
  const resolvedModel = model || getGeminiModel();
  const key = getGeminiKey();
  if (!key) throw new Error("No Gemini API key. Add one in Hayah Settings → AI Assistant.");
  const body = buildBody({
    contents: messages.map(m => ({
      role: m.role === "ai" || m.role === "model" ? "model" : "user",
      parts: messagePartsFor(m),
    })),
    systemInstruction, responseSchema, jsonMode, maxOutputTokens, temperature,
  });
  return await callGeminiResilient(resolvedModel, body, key, feature, onUsage);
}

// ── STREAMING (SSE) ──────────────────────────────────────────────────────
// Real generateContentStream — used by AI Coach text + photo paths to
// render progressively. SSE chunks are `data: <json>\n\n`; the final
// chunk carries usageMetadata. Caller passes onChunk to receive
// incremental text.
//
// Throws "Streaming not supported in this runtime" if the fetch
// Response doesn't expose a readable stream (some older WKWebView
// polyfills) — callers should fall back to the non-streaming path
// when that surfaces.
async function streamGemini(modelId, key, body, onChunk, feature) {
  const url = `${GEMINI_BASE}/${modelId}:streamGenerateContent?alt=sse&key=${key}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try { const e = await resp.json(); msg = e?.error?.message || msg; } catch {}
    throw new Error(msg.slice(0, 200));
  }
  if (!resp.body || typeof resp.body.getReader !== "function") {
    throw new Error("Streaming not supported in this runtime");
  }
  const reader  = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let accumulated = "";
  let usage = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 2);
      if (!chunk.startsWith("data:")) continue;
      const payload = chunk.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      let json;
      try { json = JSON.parse(payload); } catch { continue; }
      const txt = json?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (txt) {
        accumulated += txt;
        try { onChunk && onChunk(txt, accumulated); } catch { /* swallow caller errors */ }
      }
      if (json?.usageMetadata) usage = json.usageMetadata;
    }
  }
  if (feature && usage) {
    try { recordUsage(feature, usage, modelId); } catch {}
  }
  if (!accumulated) throw new Error("Empty stream response from Gemini");
  return { text: accumulated, usage };
}

// Walks the fallback chain for streaming. Streaming can't be retried
// mid-token so each fallback hop is a fresh request.
async function streamGeminiResilient(primaryModel, body, key, onChunk, feature, onUsage) {
  const chain = [primaryModel, ...(FALLBACK_CHAINS[primaryModel] || [])];
  let lastErr;
  for (let i = 0; i < chain.length; i++) {
    try {
      const result = await streamGemini(chain[i], key, body, onChunk, feature);
      if (typeof onUsage === "function") {
        try { onUsage({ usage: result.usage || null, modelId: chain[i] }); } catch {}
      }
      return result;
    } catch (e) {
      lastErr = e;
      const retryable = RETRYABLE.test(e.message || "") || /Streaming not supported/i.test(e.message || "");
      if (!retryable) throw e;
      if (i < chain.length - 1) await new Promise(r => setTimeout(r, 800));
    }
  }
  throw lastErr || new Error("Gemini stream failed");
}

export async function callGeminiChatStream(messages, {
  systemInstruction = null,
  model = null,
  maxOutputTokens = 4096,
  temperature = 0.7,
  feature = null,
  onChunk = null,
  onUsage = null,
} = {}) {
  const resolvedModel = model || getGeminiModel();
  const key = getGeminiKey();
  if (!key) throw new Error("No Gemini API key. Add one in Hayah Settings → AI Assistant.");
  const body = buildBody({
    contents: messages.map(m => ({
      role: m.role === "ai" || m.role === "model" ? "model" : "user",
      parts: messagePartsFor(m),
    })),
    systemInstruction, maxOutputTokens, temperature,
  });
  const { text } = await streamGeminiResilient(resolvedModel, body, key, onChunk, feature, onUsage);
  return text;
}

export async function callGeminiVisionStream(userPrompt, imageDataUrl, {
  systemInstruction = null,
  model = null,
  maxOutputTokens = 2048,
  temperature = 0.3,
  feature = null,
  onChunk = null,
  onUsage = null,
} = {}) {
  const resolvedModel = model || getGeminiModel();
  const key = getGeminiKey();
  if (!key) throw new Error("No Gemini API key. Add one in Hayah Settings → AI Assistant.");
  let mimeType = "image/jpeg", base64Data = imageDataUrl;
  if (imageDataUrl.startsWith("data:")) {
    const [header, data] = imageDataUrl.split(",");
    mimeType = header.replace("data:", "").replace(";base64", "");
    base64Data = data;
  }
  const body = buildBody({
    contents: [{
      role: "user",
      parts: [
        { text: userPrompt },
        { inline_data: { mime_type: mimeType, data: base64Data } },
      ],
    }],
    systemInstruction, maxOutputTokens, temperature,
  });
  const { text } = await streamGeminiResilient(resolvedModel, body, key, onChunk, feature, onUsage);
  return text;
}

// ── SELF-TEST ────────────────────────────────────────────────────────────
// Settings → AI Assistant → Test button. v2 trims to maxOutputTokens: 1
// since we only care about resp.ok, not the actual text content (Step 9).
export async function testGemini(key, modelId) {
  const body = {
    contents: [{ role: "user", parts: [{ text: "ok" }] }],
    generationConfig: { maxOutputTokens: 1 },
  };
  try {
    const resp = await fetch(
      `${GEMINI_BASE}/${modelId}:generateContent?key=${key}`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
    );
    const text = await resp.text();
    return { status: resp.status, ok: resp.ok, body: text };
  } catch (e) {
    return { status: 0, ok: false, body: e.message };
  }
}
